import React, { useState, useCallback, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
//Global State using JOTAI
import { useAtom } from "jotai";
import { gbState } from "../../components/shared/GlobalState";

import { ca_main_categories, main_admin_categories } from "./MenuData";
import "./SideMenu.css"; // We'll create this file for custom styles

const SideMenu = ({ isOpen, logo, userRole }) => {
  const [openSubmenus, setOpenSubmenus] = useState([]);
  const [userStatus, setUserStatus] = useAtom(gbState);

  const location = useLocation();
  const navigate = useNavigate();

  const toggleSubmenu = useCallback((index) => {
    setOpenSubmenus((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  }, []);

  const isSubmenuOpen = useCallback(
    (index) => openSubmenus.includes(index),
    [openSubmenus]
  );

  const menuItems = ca_main_categories;
  const adminMenuItems = main_admin_categories;

  const findParentMenuIndex = useCallback(
    (path) => {
      const allMenuItems = [...menuItems, ...adminMenuItems];
      return allMenuItems.findIndex((item) =>
        item.submenu?.some((subItem) => subItem.to === path)
      );
    },
    [menuItems, adminMenuItems]
  );

  useEffect(() => {
    const parentIndex = findParentMenuIndex(location.pathname);
    if (parentIndex !== -1 && !isSubmenuOpen(parentIndex)) {
      setOpenSubmenus((prev) => [...prev, parentIndex]);
    }
  }, [location.pathname, findParentMenuIndex, isSubmenuOpen]);

  const handleItemClick = useCallback(
    (item, index) => {
      if (item.submenu && item.submenu.length > 0) {
        toggleSubmenu(index);
      } else if (item.to) {
        navigate(item.to);
      }
    },
    [toggleSubmenu, navigate]
  );

  const handleSubmenuItemClick = useCallback(
    (e, to) => {
      e.preventDefault();
      e.stopPropagation();
      navigate(to);
    },
    [navigate]
  );

  return (
    <>
      <div
        className={`fixed inset-0 bg-gray-600 bg-opacity-75 z-20 transition-opacity duration-300 ${
          isOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        } md:hidden overflow-y-auto`}
      ></div>

      <div
        className={`bg-gray-800 text-white fixed md:relative z-30 transition-all duration-300 ease-in-out ${
          isOpen ? "w-64" : "w-16"
        } h-screen flex flex-col shadow-lg`}
      >
        <div className="flex items-center justify-center flex-shrink-0 w-full p-4 border-b border-gray-700">
          <img
            src={logo}
            alt="Church Logo"
            className={`transition-all duration-300 ${
              isOpen ? "w-32" : "w-8 h-8"
            } object-contain`}
          />
        </div>

        <nav className="flex-grow overflow-hidden hover:overflow-y-auto">
          <div className="h-full">
            <ul className="py-2">
              {menuItems.map(
                (item, index) =>
                  item.role === userStatus?.user?.role && (
                    <li key={index} className="relative px-2">
                      <div>
                        <button
                          onClick={() => handleItemClick(item, index)}
                          className={`flex items-center w-full p-2 text-left transition-all duration-200 rounded-md hover:bg-blue-600 ${
                            isOpen ? "px-4" : "justify-center px-2"
                          } ${isSubmenuOpen(index) ? "bg-gray-700" : ""} ${
                            location.pathname === item.to ? "bg-gray-700" : ""
                          }`}
                          title={!isOpen ? item.label : ""}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className={`w-5 h-5 transition-all duration-300 ${
                              location.pathname === item.to
                                ? "text-blue-400"
                                : ""
                            }`}
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d={item.icon} />
                          </svg>
                          {isOpen && (
                            <>
                              <span className="flex-grow ml-3 text-sm font-medium whitespace-nowrap">
                                {item.label}
                              </span>
                              {item.submenu && item.submenu.length > 0 && (
                                <svg
                                  className={`w-4 h-4 transition-transform duration-200 ${
                                    isSubmenuOpen(index)
                                      ? "transform rotate-180"
                                      : ""
                                  }`}
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <polyline points="6 9 12 15 18 9"></polyline>
                                </svg>
                              )}
                            </>
                          )}
                        </button>
                        {item.submenu && item.submenu.length > 0 && (
                          <div
                            className={`overflow-hidden transition-all duration-300 ease-in-out ${
                              isOpen
                                ? "pl-4"
                                : "absolute top-0 left-full ml-1 w-48 bg-gray-800 rounded-md shadow-lg"
                            }`}
                            style={{
                              maxHeight: isSubmenuOpen(index) ? "1000px" : "0",
                              opacity: isSubmenuOpen(index) ? 1 : 0,
                              visibility: isSubmenuOpen(index)
                                ? "visible"
                                : "hidden",
                            }}
                          >
                            <ul className="py-1">
                              {item.submenu.map((subItem, subIndex) => (
                                <li key={subIndex}>
                                  <a
                                    href={subItem.to}
                                    className={`flex items-center py-2 px-4 text-sm transition-colors duration-200 hover:bg-blue-600 rounded-md ${
                                      location.pathname === subItem.to
                                        ? "bg-gray-700 text-blue-400"
                                        : ""
                                    } ${isOpen ? "" : "justify-center"}`}
                                    onClick={(e) =>
                                      handleSubmenuItemClick(e, subItem.to)
                                    }
                                    title={!isOpen ? subItem.label : ""}
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      className={`w-4 h-4 ${
                                        isOpen ? "mr-2" : ""
                                      } ${
                                        location.pathname === subItem.to
                                          ? "text-blue-400"
                                          : ""
                                      }`}
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    >
                                      <path d={subItem.icon} />
                                    </svg>
                                    {isOpen && (
                                      <span className="text-sm whitespace-nowrap">
                                        {subItem.label}
                                      </span>
                                    )}
                                  </a>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </li>
                  )
              )}
            </ul>

            {userStatus?.user?.role === "FULL_ACCESS" && (
              <>
                <div className="px-4 mt-6 mb-2">
                  <h3 className="text-xs font-semibold tracking-wider text-gray-400 uppercase">
                    Administration
                  </h3>
                </div>
                <ul className="px-2 space-y-1">
                  {adminMenuItems
                    .filter(
                      (item) =>
                        !item.role ||
                        item.role === userStatus?.user?.role ||
                        userStatus?.user?.role === "FULL_ACCESS"
                    )
                    .map((item, index) => (
                      <li key={index} className="relative">
                        <Link
                          to={item.to}
                          className={`flex items-center p-2 transition-colors duration-200 hover:bg-gray-700 ${
                            isOpen ? "px-4" : "justify-center px-2"
                          } ${
                            location.pathname === item.to ? "bg-gray-700" : ""
                          }`}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="w-5 h-5 transition-all duration-300"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d={item.icon} />
                          </svg>
                          {isOpen && (
                            <span className="ml-3 text-sm font-medium">
                              {item.label}
                            </span>
                          )}
                        </Link>
                      </li>
                    ))}
                </ul>
              </>
            )}
          </div>
        </nav>
      </div>
    </>
  );
};

export default SideMenu;
